import "./ReservationSidebarInvoices.scss"
import noInvoicesImg from "assets/img/no-invoices.svg"
import IconThreeDots from "assets/img/icon-three-dots.svg?react"

import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { Trans, useTranslation } from "react-i18next"
import { Dayjs } from "utils/dayjs"

import Routes from "@/routes"
import { Flex, FlexItem } from "components/Flex"
import Text from "components/Text/Text"
import ReservationSidebarInvoice from "./ReservationSidebarInvoice"
import { useInterfaceStatuses } from "stores/interfaceStatuses"
import { Guid } from "@/types"
import useReservationInvoices from "hooks/useReservationInvoices"
import Spinner from "components/Spinner/Spinner"
import { Grid, GridItem } from "components/Grid"
import Button from "components/Button/Button"
import { Currency, Purpose, UserPermission } from "config/constants"
import useExchangeRates from "hooks/useExchangeRates"
import { IGetExchangeRateRequest } from "models/ExchangeRate"
import useTenantCurrency from "hooks/useTenantCurrency"
import { applyExchangeRate } from "stores/exchangeRates"
import { InfoDropdown, InfoDropdownList, InfoDropdownListItemButton, InfoDropdownListItemLink } from "components/InfoDropdown"
import useMessageListener from "modules/WindowMessager/hooks/useMessageListener"
import { MessageName } from "modules/WindowMessager/constants"
import { usePermissions } from "stores/permissions"
import { OutgoingInvoiceStatus, ReservationOutgoingInvoice } from "models/OutgoingInvoice"
import Status, { StatusType } from "components/Status/Status"
import AssetService from "services/AssetService"
import toastMessages from "stores/toastMessages"
import BulkSendToErpModal from "./BulkSendToErpModal"

interface IReservationSidebarInvoicesProps {
    reservationId: Guid
    currency: Currency | null
    purpose: Purpose
    disposalDate: Dayjs | null
    onNoteCreated(): Promise<void>
    onInvoiceBlocked(): Promise<void>
}

enum InvoicesSelectionMode {
    collectiveCreditNote = "collectiveCreditNote",
    bulkSendToErp = "bulkSendToErp",
}

const SYNC_REFRESH_DELAY_MS = 500

const ReservationSidebarInvoices: FC<IReservationSidebarInvoicesProps> = ({
    reservationId,
    currency,
    purpose,
    disposalDate,
    onNoteCreated,
    onInvoiceBlocked,
}) => {
    const { t } = useTranslation()
    const location = useLocation()
    const tenantCurrency = useTenantCurrency()
    const { hasPermission } = usePermissions()
    const { isInvoiceERPInterfaceConnected } = useInterfaceStatuses()
    const [selectionMode, setSelectionMode] = useState<InvoicesSelectionMode | null>(null)
    const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<Guid[]>([])
    const [isBulkSendingToErp, setIsBulkSendingToErp] = useState(false)
    const [footerHeight, setFooterHeight] = useState(0)
    const [syncRefreshTimeoutId, setSyncRefreshTimeoutId] = useState<NodeJS.Timeout | null>(null)
    const [showBulkSendToErpModal, setShowBulkSendToErpModal] = useState(false)

    const {
        invoices,
        refetchInvoices,
        canCreateAdHocInvoice,
        linkCreateCollectiveCreditNote,
        handleInvoiceResubmitted
    } = useReservationInvoices(reservationId, { hydrateInvoicePositions: true })

    useMessageListener(MessageName.refreshOutgoingInvoices, payload => {
        if (payload.reservationId !== reservationId) return
        refetchInvoices()
    })

    const exchangeRatesParams: IGetExchangeRateRequest[] = useMemo(() => {
        if (!invoices || !currency || !tenantCurrency || currency === tenantCurrency) return []
        return invoices.map(invoice => {
            const date = invoice.getExchangeRateDate({
                purpose,
                disposalDate,
            })
            if (!date) return null
            return {
                date,
                currency,
            }
        }).filter(i => !!i) as IGetExchangeRateRequest[]
    }, [currency, disposalDate, invoices, purpose, tenantCurrency])
    const { getExchangeRate } = useExchangeRates({ items: exchangeRatesParams })

    const totalInvoiceAmount = useMemo(() => {
        return invoices?.reduce((sum, invoice) => sum + invoice.amount, 0) ?? 0
    }, [invoices])

    const totalInvoiceAmountInTenantCurrency: number | null = useMemo(() => {
        if (!invoices || !currency || !tenantCurrency || currency === tenantCurrency) return null
        if (totalInvoiceAmount === 0) return 0

        let sum = 0
        for (const invoice of invoices) {
            const date = invoice.getExchangeRateDate({
                purpose,
                disposalDate,
            })
            // if no exchange rate date, we cannot calculate the sum
            if (!date) return null
            const exchangeRate = getExchangeRate({ currency, date })
            // if no exchange rate, we cannot calculate the sum
            if (!exchangeRate) return null
            sum += applyExchangeRate(invoice.amount, exchangeRate)
        }
        return sum
    }, [currency, disposalDate, getExchangeRate, invoices, purpose, tenantCurrency, totalInvoiceAmount])


    const totalRentalFee = useMemo(() => {
        return invoices?.reduce((sum, invoice) => sum + invoice.rentalFeeAmount, 0) ?? 0
    }, [invoices])

    const totalRentalFeeInTenantCurrency: number | null = useMemo(() => {
        if (!invoices || !currency || !tenantCurrency || currency === tenantCurrency) return null
        if (totalRentalFee === 0) return 0

        let sum = 0
        for (const invoice of invoices) {
            const date = invoice.getExchangeRateDate({
                purpose,
                disposalDate,
            })
            // if no exchange rate date, we cannot calculate the sum
            if (!date) return null
            const exchangeRate = getExchangeRate({ currency, date })
            // if no exchange rate, we cannot calculate the sum
            if (!exchangeRate) return null
            sum += applyExchangeRate(invoice.rentalFeeAmount, exchangeRate)
        }
        return sum
    }, [currency, disposalDate, getExchangeRate, invoices, purpose, tenantCurrency, totalRentalFee])

    function getParentInvoices(parentInvoiceIds?: Guid[]) {
        if (!invoices || !parentInvoiceIds?.length) return
        return invoices.filter(invoice => parentInvoiceIds.includes(invoice.id))
    }

    async function handleNoteCreated() {
        onNoteCreated()
        handleSelectionReset()
        await refetchInvoices()
    }

    const collectivelyCreditableInvoiceIds = useMemo(() => {
        return invoices
            ?.filter(i => i.collectivelyCreditable)
            ?.map(i => i.id) ?? []
    }, [invoices])

    const invoicesIdsToCreateCollectiveCreditNote = useMemo(() => {
        return selectedInvoiceIds.filter(invoiceId => collectivelyCreditableInvoiceIds.includes(invoiceId))
    }, [collectivelyCreditableInvoiceIds, selectedInvoiceIds])

    const invoiceIdsThatCanBeSentToErp = useMemo(() => {
        if (!isInvoiceERPInterfaceConnected || !hasPermission(UserPermission.OutgoingInvoicesCreate)) return []
        return invoices
            ?.filter(i => i.status === OutgoingInvoiceStatus.pendingErpSend)
            ?.map(i => i.id) ?? []
    }, [isInvoiceERPInterfaceConnected, hasPermission, invoices])

    const invoiceIdsToSendToErp = useMemo(() => {
        return selectedInvoiceIds.filter(invoiceId => invoiceIdsThatCanBeSentToErp.includes(invoiceId))
    }, [invoiceIdsThatCanBeSentToErp, selectedInvoiceIds])

    const amountToBeSentToErp = useMemo(() => {
        return invoiceIdsToSendToErp.reduce((sum, invoiceId) => sum + (invoices?.find(i => i.id === invoiceId)?.amount ?? 0), 0)
    }, [invoices, invoiceIdsToSendToErp])

    const handleCollectiveCreditNoteInvoiceSelectionToggle = useCallback((invoiceId: Guid) => {
        const newSelectedInvoiceIds = collectivelyCreditableInvoiceIds
            .filter(id => {
                if (invoicesIdsToCreateCollectiveCreditNote.includes(invoiceId)) {
                    return collectivelyCreditableInvoiceIds.indexOf(id) < collectivelyCreditableInvoiceIds.indexOf(invoiceId)
                } else {
                    return collectivelyCreditableInvoiceIds.indexOf(id) <= collectivelyCreditableInvoiceIds.indexOf(invoiceId)
                }
            })
        setSelectedInvoiceIds(newSelectedInvoiceIds)
    }, [invoicesIdsToCreateCollectiveCreditNote, collectivelyCreditableInvoiceIds])

    async function handleCollectiveCreditNoteInit(closeDropdown: () => void) {
        closeDropdown()
        setSelectedInvoiceIds([])
        setSelectionMode(InvoicesSelectionMode.collectiveCreditNote)
    }


    const handleBulkSendToErpInvoiceSelectionToggle = useCallback((invoiceId: Guid) => {
        if (invoiceIdsToSendToErp.includes(invoiceId)) {
            setSelectedInvoiceIds(invoiceIdsToSendToErp.filter(id => id !== invoiceId))
        } else {
            setSelectedInvoiceIds([...invoiceIdsToSendToErp, invoiceId])
        }
    }, [invoiceIdsToSendToErp])


    async function handleBulkSendToErpInit(closeDropdown: () => void) {
        closeDropdown()
        setSelectedInvoiceIds([])
        setSelectionMode(InvoicesSelectionMode.bulkSendToErp)
    }

    const getInvoiceSelectionToggleHandler = useCallback((invoice: ReservationOutgoingInvoice) => {
        if (selectionMode === InvoicesSelectionMode.collectiveCreditNote && collectivelyCreditableInvoiceIds.includes(invoice.id)) {
            return () => handleCollectiveCreditNoteInvoiceSelectionToggle(invoice.id)
        }
        if (selectionMode === InvoicesSelectionMode.bulkSendToErp && invoiceIdsThatCanBeSentToErp.includes(invoice.id)) {
            return () => handleBulkSendToErpInvoiceSelectionToggle(invoice.id)
        }
    }, [selectionMode, collectivelyCreditableInvoiceIds, invoiceIdsThatCanBeSentToErp,
        handleCollectiveCreditNoteInvoiceSelectionToggle, handleBulkSendToErpInvoiceSelectionToggle])

    const getInvoiceSelectionState = useCallback((invoice: ReservationOutgoingInvoice) => {
        return (selectionMode === InvoicesSelectionMode.collectiveCreditNote && invoicesIdsToCreateCollectiveCreditNote.includes(invoice.id))
            || (selectionMode === InvoicesSelectionMode.bulkSendToErp && invoiceIdsToSendToErp.includes(invoice.id))
    }, [invoicesIdsToCreateCollectiveCreditNote, invoiceIdsToSendToErp, selectionMode])

    function handleSelectionReset() {
        setSelectionMode(null)
        setSelectedInvoiceIds([])
    }

    function handleAddCollectiveCreditNoteClick() {
        window.open(Routes.cancelInvoices.getPath({}, { reservationId, invoiceIds: invoicesIdsToCreateCollectiveCreditNote }), "_blank")
        handleSelectionReset()
    }

    async function handleBulkSendToErpClick() {
        if (!invoiceIdsToSendToErp.length) return

        if (invoiceIdsToSendToErp.length > 1) {
            setShowBulkSendToErpModal(true)
            return
        }

        setIsBulkSendingToErp(true)
        try {
            const invoice = invoices?.find(i => i.id === invoiceIdsToSendToErp[0])
            await AssetService.sendInvoicesToErp({
                invoiceIds: [invoiceIdsToSendToErp[0]],
                headerText: invoice?.headerText ?? null,
                footerText: invoice?.footerText ?? null,
            })
            refreshInvoicesWithTimeout()
        } catch (e) {
            setIsBulkSendingToErp(false)
            toastMessages.showSystemError()
        }
    }

    async function handleBulkSendToErp(data: { headerText: string, footerText: string }) {
        setShowBulkSendToErpModal(false)
        setIsBulkSendingToErp(true)
        try {
            await AssetService.sendInvoicesToErp({
                invoiceIds: invoiceIdsToSendToErp,
                headerText: data.headerText || null,
                footerText: data.footerText || null,
            })
            refreshInvoicesWithTimeout()
        } catch (e) {
            setIsBulkSendingToErp(false)
            toastMessages.showSystemError()
        }
    }

    const bulkSendToErpText: { headerText: string, footerText: string } | null = useMemo(() => {
        if (!invoiceIdsToSendToErp.length || !invoices?.length) return null

        const headerTextArr: string[] = []
        const footerTextArr: string[] = []

        invoices.forEach(invoice => {
            if (invoiceIdsToSendToErp.includes(invoice.id)) {
                headerTextArr.push(invoice.headerText ?? "")
                footerTextArr.push(invoice.footerText ?? "")
            }
        })

        return {
            headerText: headerTextArr.filter(Boolean).join("\n\n------\n\n"),
            footerText: footerTextArr.filter(Boolean).join("\n\n------\n\n"),
        }
    }, [invoiceIdsToSendToErp, invoices])

    function refreshInvoicesWithTimeout() {
        setSyncRefreshTimeoutId(
            // as sync operation is async on backend, we have to wait before refetching invoices
            setTimeout(async () => {
                await refetchInvoices()
                setIsBulkSendingToErp(false)
                handleSelectionReset()
            }, SYNC_REFRESH_DELAY_MS)
        )
    }

    useEffect(() => {
        return () => {
            if (syncRefreshTimeoutId) {
                clearTimeout(syncRefreshTimeoutId)
            }
        }
    }, [syncRefreshTimeoutId])

    const footerRef = useCallback((node: HTMLDivElement | null) => {
        if (!node) {
            setFooterHeight(0)
            return
        }
        const resizeObserver = new ResizeObserver(() => {
            if (node) {
                setFooterHeight(node.offsetHeight - 64 + 24) // 64 for sidebar content padding bottom, 24 for additional gap
            } else {
                setFooterHeight(0)
            }
        })
        resizeObserver.observe(node)
    }, [])

    if (!invoices) {
        return <Spinner className="py-8" />
    }

    if (invoices.length === 0) {
        return (
            <Flex className="p-4 h-100"
                alignItems="center"
                justifyContent="center"
                flow="column">
                <img src={noInvoicesImg} alt="" className="mb-4" />
                <Text className="text-center">
                    {t('requestDetails.noInvoices1')}<br />
                    <Trans i18nKey="requestDetails.noInvoices2">
                        You will get notified on the <Link to={Routes.dashboard.getPath()} className="link color-brand-blue">dashboard</Link> if it is time to create an invoice for this request.
                    </Trans>
                </Text>
            </Flex>
        )
    }

    return (
        <>
            <div style={{ paddingBottom: footerHeight }}>
                <Flex className="mb-4" gap="2.4rem" alignItems="center" justifyContent="space-between">
                    <Text type="h3">{t('requestDetails.outgoingInvoices')}</Text>
                    {(canCreateAdHocInvoice || !!linkCreateCollectiveCreditNote || invoiceIdsThatCanBeSentToErp.length > 0) &&
                        <InfoDropdown
                            label={() => (
                                <Button
                                    theme="grey"
                                    round
                                    icon={<IconThreeDots />}
                                />
                            )}
                            placement="bottom-end"
                            content={closeDropdown => (
                                <InfoDropdownList>
                                    {canCreateAdHocInvoice &&
                                        <InfoDropdownListItemLink
                                            href={Routes.adHocOutgoingInvoiceCreate.getPath({ reservationId }, { backUrl: location.pathname + location.search })}>
                                            {t('adHocInvoice.addAdHocInvoice')}
                                        </InfoDropdownListItemLink>
                                    }
                                    {!!linkCreateCollectiveCreditNote &&
                                        <InfoDropdownListItemButton
                                            onClick={() => handleCollectiveCreditNoteInit(closeDropdown)}>
                                            {t('noteCreate.createCollectiveCreditNote')}
                                        </InfoDropdownListItemButton>
                                    }
                                    {invoiceIdsThatCanBeSentToErp.length > 0 &&
                                        <InfoDropdownListItemButton
                                            onClick={() => handleBulkSendToErpInit(closeDropdown)}>
                                            {t('outgoingInvoices.sendToErp')}
                                        </InfoDropdownListItemButton>
                                    }
                                </InfoDropdownList>
                            )}
                        />
                    }
                </Flex>
                <Grid columns="1fr 1fr" gap="2.4rem" className="mb-4">
                    <GridItem className="bg-grey-50 round-sm px-1-5 pb-1-5 pt-2">
                        <Text type="pSmallest">{t('requestDetails.totalInvoiceAmount')}</Text>
                        <div className="font-bold mt-half">
                            {t('common.amountWithCurrency', {
                                count: totalInvoiceAmount,
                                currency,
                            })}
                        </div>
                        {typeof totalInvoiceAmountInTenantCurrency === "number" && !!tenantCurrency &&
                            <Text type="pSmallest" className="color-grey-500">
                                {t('common.amountWithCurrency', {
                                    count: totalInvoiceAmountInTenantCurrency,
                                    currency: tenantCurrency,
                                })}
                            </Text>
                        }
                    </GridItem>
                    <GridItem className="bg-grey-50 round-sm px-1-5 pb-1-5 pt-2">
                        <Text type="pSmallest">{t('requestDetails.totalRentalFees')}</Text>
                        <div className="font-bold mt-half">
                            {t('common.amountWithCurrency', {
                                count: totalRentalFee,
                                currency,
                            })}
                        </div>
                        {typeof totalRentalFeeInTenantCurrency === "number" && !!tenantCurrency &&
                            <Text type="pSmallest" className="color-grey-500">
                                {t('common.amountWithCurrency', {
                                    count: totalRentalFeeInTenantCurrency,
                                    currency: tenantCurrency,
                                })}
                            </Text>
                        }
                    </GridItem>
                </Grid>
                {invoices.map(invoice => (
                    <div key={invoice.id} className="mb-2">
                        <ReservationSidebarInvoice
                            reservationId={reservationId}
                            reservationInvoice={invoice}
                            parentInvoicesErp={getParentInvoices(invoice.parentInvoiceIds)?.map(ip => ({ erpNumber: ip.erpOrderNumber, erpNumberStatus: ip.erpOrderNumberStatus }))}
                            onNoteCreated={handleNoteCreated}
                            onInvoiceBlocked={onInvoiceBlocked}
                            onSelectionToggle={getInvoiceSelectionToggleHandler(invoice)}
                            isSelected={getInvoiceSelectionState(invoice)}
                            refreshInvoices={refetchInvoices}
                            currency={currency}
                            exchangeRateDate={invoice.getExchangeRateDate({ purpose, disposalDate })}
                            isInvoiceERPInterfaceConnected={isInvoiceERPInterfaceConnected}
                            disabled={selectionMode === InvoicesSelectionMode.bulkSendToErp}
                            onInvoiceResubmitted={(invoiceId: Guid) => handleInvoiceResubmitted(invoiceId)}
                            resubmitInvoiceLink={invoice.linkResubmitInvoice}
                        />
                    </div>
                ))}
            </div>
            {selectionMode === InvoicesSelectionMode.collectiveCreditNote &&
                <div ref={footerRef} className="reservation-sidebar-invoices__selection-footer p-4 bg-white border-top-grey-100">
                    <Text className="color-grey-500 mb-2 text-right">{t('noteCreate.collectiveCreditNoteSelectInvoice')}</Text>
                    <Flex alignItems="center" justifyContent="flex-end" gap="2.4rem">
                        <Button
                            onClick={handleSelectionReset}
                            theme="secondary"
                            label={t('common.cancel')}
                        />
                        <Button
                            onClick={handleAddCollectiveCreditNoteClick}
                            label={t('noteCreate.createCollectiveCreditNote')}
                            disabled={!invoicesIdsToCreateCollectiveCreditNote.length}
                        />
                    </Flex>
                </div>
            }
            {selectionMode === InvoicesSelectionMode.bulkSendToErp &&
                <div ref={footerRef} className="reservation-sidebar-invoices__selection-footer p-4 bg-white border-top-grey-100">
                    <Text className="color-grey-500 mb-2 text-right">{t('outgoingInvoices.selectInvoicesToSendToErp')}</Text>
                    {amountToBeSentToErp === 0 && invoiceIdsToSendToErp.length > 0 &&
                        <div className="mb-3">
                            <Status type={StatusType.warning}>
                                {t('outgoingInvoices.amountToBeSentToErpIsZero')}
                            </Status>
                        </div>
                    }
                    <Flex alignItems="center" justifyContent="space-between" gap="2.4rem">
                        <FlexItem className="color-grey-500">
                            {t('outgoingInvoices.amountToBeSentToErp')}: <strong>{t('common.amountWithCurrency', {
                                count: amountToBeSentToErp,
                                currency,
                            })}</strong>
                        </FlexItem>
                        <Flex alignItems="center" justifyContent="flex-end" gap="2.4rem">
                            <Button
                                onClick={handleSelectionReset}
                                theme="secondary"
                                label={t('common.cancel')}
                                disabled={isBulkSendingToErp}
                            />
                            <Button
                                onClick={handleBulkSendToErpClick}
                                label={t('outgoingInvoices.sendToErp')}
                                disabled={!invoiceIdsToSendToErp.length || amountToBeSentToErp === 0 || isBulkSendingToErp}
                                loading={isBulkSendingToErp}
                            />
                        </Flex>
                    </Flex>
                </div>
            }
            {showBulkSendToErpModal &&
                <BulkSendToErpModal
                    headerText={bulkSendToErpText?.headerText ?? ""}
                    footerText={bulkSendToErpText?.footerText ?? ""}
                    totalAmount={amountToBeSentToErp}
                    currency={currency}
                    onClose={() => setShowBulkSendToErpModal(false)}
                    onConfirm={handleBulkSendToErp}
                />
            }
        </>
    )
}

export default ReservationSidebarInvoices
